// src/services/chatActions.js — pin / archive / delete, written with the app's
// exact Firestore semantics so every action syncs to the phones instantly:
//
//   pin      chats/{id}.pinned.{uid} = bool          (ChatsTab.handleTogglePin)
//   archive  chats/{id}.archived.{uid} = bool        (ChatsTab.handleArchiveChat)
//   delete   1:1 → deletedFor.{uid} + unreadCounts.{uid}=0 + clearedAt.{uid}
//            group, still in it → "delete & exit": leave participants, join
//            leftParticipants, post the app's user_left system message, bump
//            everyone else's unread, stamp lastMessage/lastMessageSystem —
//            byte-for-byte what ChatsTab.handleDeleteChat writes
//            group, already exited → deletedFor.{uid} only (nothing to exit)

import {
  doc, getDoc, updateDoc, deleteDoc, addDoc, collection, arrayUnion, serverTimestamp, increment,
} from 'firebase/firestore';
import { adminsAfterLeave } from './groupLeave';
import { db } from '../config/firebase';

export const isArchived = (chat, uid) => chat?.archived?.[uid] === true;
export const hasExitedGroup = (chat, uid) =>
  !!chat?.isGroup && (chat?.leftParticipants || []).includes(uid);

export function togglePin(chat, uid) {
  return updateDoc(doc(db, 'chats', chat.id), { [`pinned.${uid}`]: !chat?.pinned?.[uid] });
}

export function setArchived(chat, uid, archive) {
  // the app refuses to archive a deleted chat — same guard
  if (chat?.deletedFor?.[uid] === true) return Promise.resolve();
  return updateDoc(doc(db, 'chats', chat.id), { [`archived.${uid}`]: !!archive });
}

/** 1:1 (or an exited group): hide for me, zero my badge, set the cleared watermark. */
export function deleteChatForMe(chat, uid) {
  return updateDoc(doc(db, 'chats', chat.id), {
    [`deletedFor.${uid}`]: true,
    [`unreadCounts.${uid}`]: 0,
    [`clearedAt.${uid}`]: serverTimestamp(),
  });
}

/** Group I'm still in: exit it AND delete it — the app's Delete flow for groups. */
export async function deleteAndExitGroup(chat, uid, myName) {
  const chatRef = doc(db, 'chats', chat.id);
  const snap = await getDoc(chatRef);
  if (!snap.exists()) return;
  const data = snap.data();

  // already out (removed by an admin, or exited on the phone) → plain delete
  if ((data.leftParticipants || []).includes(uid) || !(data.participants || []).includes(uid)) {
    await deleteChatForMe(chat, uid);
    return;
  }

  const updatedParticipants = (data.participants || []).filter((id) => id !== uid);
  const stillIn = updatedParticipants.filter((id) => !(data.leftParticipants || []).includes(id));
  if (stillIn.length === 0) {
    // last one out turns off the lights — the app deletes the group doc entirely
    await deleteDoc(chatRef);
    return;
  }

  const name = myName || 'A member';
  const text = `${name} left the group`;
  // the "X left the group" system message everyone else sees
  await addDoc(collection(db, 'chats', chat.id, 'messages'), {
    type: 'system',
    systemType: 'user_left',
    sysKind: 'user_left',
    sysParams: { name },
    text,
    senderName: name,
    timestamp: serverTimestamp(),
    senderId: uid,
  });
  // exit + notify, one write — participants out, left history in, hidden for me,
  // preview + unread bumped for everyone still inside
  const left = data.leftParticipants || [];
  const bumps = {};
  for (const u of data.participants || []) {
    if (u !== uid && !left.includes(u)) bumps[`unreadCounts.${u}`] = increment(1);
  }
  const admins = adminsAfterLeave(data, uid);   // the role passes on when the last admin goes
  await updateDoc(chatRef, {
    participants: updatedParticipants,
    leftParticipants: arrayUnion(uid),
    [`deletedFor.${uid}`]: true,
    lastMessageSystem: { type: 'user_left', name, sysKind: 'user_left', sysParams: { name } },
    lastMessage: text,
    timestamp: serverTimestamp(),
    ...bumps,
    ...(admins ? { admins } : {}),
  });
}
